import React, { useState, useEffect, useRef } from 'react';

const BONANZA_DURATION_SECONDS = 1800; // 30 minutes

export function BonanzaCountdown({ pool, qualifiers, onBonanzaStart, onBonanzaEnd, dropLeaderboard, droppingsLeaderboard, playerWallet, bonanzaStatus }) {
  const [phase, setPhase] = useState('announcing'); // announcing, countdown, running, complete
  const [timeLeft, setTimeLeft] = useState(() =>
    bonanzaStatus?.secondsRemaining != null ? bonanzaStatus.secondsRemaining : BONANZA_DURATION_SECONDS
  );
  const [winner, setWinner] = useState(null);
  const [winnerSpotNum, setWinnerSpotNum] = useState(null);
  const [chickenPos, setChickenPos] = useState(0);
  const [walkPath, setWalkPath] = useState([]);
  const [showEgg, setShowEgg] = useState(false);
  const [shuffledPlayers, setShuffledPlayers] = useState([]);
  const timerRef = useRef(null);
  const walkRef = useRef(null);
  const audioRef = useRef(null);

  const prize = pool.toFixed(3);

  const isQualified = playerWallet && (
    (dropLeaderboard || []).slice(0, 100).some(p => p.fullWallet === playerWallet) ||
    (droppingsLeaderboard || []).slice(0, 100).some(p => p.fullWallet === playerWallet)
  );


  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      clearInterval(walkRef.current);
    };
  }, []);

  useEffect(() => {
    if (phase === 'running') {
      const allPlayers = [...(dropLeaderboard || []).slice(0, 100), ...(droppingsLeaderboard || []).slice(0, 100)];
      const shuffled = [...allPlayers].sort(() => Math.random() - 0.5);
      const spots = Array(100).fill(null);
      shuffled.forEach((player, i) => { if (i < 100) spots[i] = player; });
      setShuffledPlayers(spots);
    }
  }, [phase]);

  useEffect(() => {
    if (phase !== 'countdown' && phase !== 'running') return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]);

  useEffect(() => {
    const t = setTimeout(() => setPhase('countdown'), 5000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (timeLeft === 0 && phase === 'countdown') {
      startBonanzaRound();
    }
  }, [timeLeft, phase]);

  useEffect(() => {
    if (phase === 'countdown') {
      if (!audioRef.current) {
        audioRef.current = new Audio('/Bonanza-countdown-music.mp3');
        audioRef.current.loop = true;
      }
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, [phase]);

  const startBonanzaRound = () => {
    if (onBonanzaStart) onBonanzaStart();
    const totalSpots = 100;
    const winnerSpot = Math.floor(Math.random() * totalSpots) + 1;
    const wanderSteps = 15 + Math.floor(Math.random() * 10);
    const path = [];
    for (let i = 0; i < wanderSteps; i++) {
      path.push(Math.floor(Math.random() * totalSpots) + 1);
    }
    path.push(winnerSpot);
    setWalkPath(path);
    setWinnerSpotNum(winnerSpot);
    setChickenPos(0);

    let step = 0;
    walkRef.current = setInterval(() => {
      step++;
      setChickenPos(step);
      if (step >= path.length) {
        clearInterval(walkRef.current);
        setTimeout(() => {
          setWinner(winnerSpot);
          setShowEgg(true);
          setTimeout(() => {
            setPhase('complete');
          }, 5000);
        }, 1000);
      }
    }, 2000);
  };

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const COLS = 10;
  const CELL = 48;
  const W = COLS * CELL;
  const H = COLS * CELL;

  const top90Drop = (dropLeaderboard || []).slice(0, 100);
  const top10Drop = (droppingsLeaderboard || []).slice(0, 100);
  const allQualifiers = [...top90Drop, ...top10Drop];

  const getWinnerInfo = (spotNum) => {
    if (spotNum <= 100) return top90Drop[spotNum - 1];
    return top10Drop[spotNum - 101];
  };

  const winnerInfo = winner ? getWinnerInfo(winner) : null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 500,
      background: 'rgba(0,0,0,0.97)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      fontFamily: 'sans-serif', color: 'white',
      overflowY: 'auto', padding: '20px',
    }}>
      <style>{`
        @keyframes bonanzaGlow {
          0%, 100% { text-shadow: 0 0 20px rgba(240,184,0,0.5); }
          50% { text-shadow: 0 0 60px rgba(240,184,0,1), 0 0 100px rgba(240,184,0,0.5); }
        }
        @keyframes bonanzaPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
        @keyframes eggDrop {
          0% { transform: scale(0) rotate(-20deg); opacity: 0; }
          60% { transform: scale(1.3) rotate(5deg); opacity: 1; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
      `}</style>

      {/* ANNOUNCING */}
      {phase === 'announcing' && isQualified && (
        <div style={{ textAlign: 'center', maxWidth: 700 }}>
          <div style={{ fontSize: 72, marginBottom: 16 }}>🎰</div>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 36, color: '#F0B800', letterSpacing: 4, marginBottom: 20, animation: 'bonanzaGlow 2s ease infinite', lineHeight: 1.3 }}>
            🎰 THE BONANZA HAS BEEN TRIGGERED AND LOCKED — YOU ARE TOP 100 ON THE LEADERBOARD AND HAVE WON A SPOT IN THE BONANZA! 🎰
          </div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 16, color: '#fff', lineHeight: 2, marginBottom: 24, background: '#111', border: '2px solid #F0B800', borderRadius: 12, padding: '20px 32px' }}>
            <div style={{ fontSize: 18, color: '#F0B800', marginBottom: 8 }}>⚡ HOW IT WORKS ⚡</div>
            Each qualifying player will be{' '}<span style={{ color: '#F0B800' }}>randomly assigned a square</span>{' '}on the arena.<br />
            The chicken will wander the grid and lay a{' '}<span style={{ color: '#F0B800' }}>GOLDEN EGG</span>{' '}on one square.<br /><br />
            <div style={{ fontSize: 20, fontWeight: 'bold', color: '#4CAF50' }}>
              IF YOUR SQUARE GETS A GOLDEN EGG LAID ON IT —<br />
              YOU WIN THE BONANZA POOL OF {prize} SOL!!!!
            </div>
          </div>
          <div style={{ fontSize: 20, color: '#F0B800', letterSpacing: 4, marginBottom: 8 }}>🎰 THE CHICKEN DROP BONANZA ROUND IS STARTING IN:</div>
          <div style={{ fontSize: 96, fontWeight: 900, color: '#F0B800', letterSpacing: 8, animation: 'bonanzaGlow 1s ease infinite', lineHeight: 1, marginBottom: 16 }}>
            {formatTime(timeLeft)}
          </div>
          <div style={{ fontSize: 28, color: '#fff', marginBottom: 24 }}>
            YOU COULD WIN THE PRIZE POOL: <span style={{ color: '#F0B800' }}>{prize} SOL</span>
          </div>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 24, flexWrap: 'wrap' }}>
            <a href="/drop/bonanza" style={{
              display: 'inline-block',
              background: '#F0B800',
              color: '#000',
              padding: '12px 28px',
              borderRadius: 8,
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: 14,
              fontFamily: "'DM Mono', monospace",
              letterSpacing: '1px',
            }}>
              🥚 WATCH THE CHICKEN DROP BONANZA ARENA!!
            </a>
          </div>
        </div>
      )}

      {/* ANNOUNCING — SPECTATOR */}
      {phase === 'announcing' && !isQualified && (
        <div style={{ textAlign: 'center', maxWidth: 700 }}>
          <div style={{ fontSize: 72, marginBottom: 16 }}>🎰</div>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 48, color: '#F0B800', letterSpacing: 6, marginBottom: 12, animation: 'bonanzaGlow 2s ease infinite' }}>
            SOMEONE IS ABOUT TO WIN THE CHICKEN DROP BONANZA!!!
          </div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 16, color: '#fff', lineHeight: 2, marginBottom: 24, background: '#111', border: '2px solid #F0B800', borderRadius: 12, padding: '20px 32px' }}>
            <div style={{ fontSize: 18, color: '#F0B800', marginBottom: 8 }}>⚡ HOW IT WORKS ⚡</div>
            <div style={{ fontSize: 20, fontWeight: 'bold', color: '#4CAF50' }}>
              The TOP 100 on the Chicken Droppings Leaderboard have a chance to win THE BONANZA POOL OF {pool} SOL!!!!
            </div>
          </div>
          <div style={{ fontSize: 20, color: '#F0B800', letterSpacing: 4, marginBottom: 8 }}>🎰 THE CHICKEN DROP BONANZA ROUND IS STARTING IN:</div>
          <div style={{ fontSize: 96, fontWeight: 900, color: '#F0B800', letterSpacing: 8, animation: 'bonanzaGlow 1s ease infinite', lineHeight: 1, marginBottom: 16 }}>
            {formatTime(timeLeft)}
          </div>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 24, flexWrap: 'wrap' }}>
            <a href="/drop/bonanza" style={{
              display: 'inline-block',
              background: '#F0B800',
              color: '#000',
              padding: '12px 28px',
              borderRadius: 8,
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: 14,
              fontFamily: "'DM Mono', monospace",
              letterSpacing: '1px',
            }}>
              🥚 WATCH THE CHICKEN DROP BONANZA ARENA!!
            </a>
          </div>
        </div>
      )}

      {/* COUNTDOWN */}
      {phase === 'countdown' && (
        <div style={{ textAlign: 'center', width: '100%', maxWidth: 800 }}>
          <div style={{ fontSize: 20, color: '#F0B800', letterSpacing: 4, marginBottom: 8 }}>🎰 THE CHICKEN DROP BONANZA ROUND IS STARTING IN:</div>
          <div style={{ fontSize: 96, fontWeight: 900, color: '#F0B800', letterSpacing: 8, animation: 'bonanzaGlow 1s ease infinite', lineHeight: 1, marginBottom: 16 }}>
            {formatTime(timeLeft)}
          </div>
          <div style={{ fontSize: 28, color: '#fff', marginBottom: 32 }}>
            Prize Pool: <span style={{ color: '#F0B800' }}>{prize} SOL</span>
          </div>

          {isQualified ? (
            <div style={{ marginTop: 24, background: '#111', border: '2px solid #F0B800', borderRadius: 12, padding: '20px 32px', maxWidth: 600, width: '100%', margin: '24px auto 0 auto', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: '#4CAF50', letterSpacing: 3, marginBottom: 12 }}>
                🏆 CONGRATULATIONS — YOU ARE IN THE BONANZA! 🏆
              </div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 15, color: '#fff', lineHeight: 2 }}>
                Each qualifying player will be <span style={{ color: '#F0B800' }}>randomly assigned a square</span> on the arena.<br />
                The chicken will wander the grid and lay a <span style={{ color: '#F0B800' }}>GOLDEN EGG</span> on one square.<br /><br />
                <div style={{ fontSize: 20, fontWeight: 'bold', color: '#4CAF50' }}>
                  IF YOUR SQUARE GETS A GOLDEN EGG LAID ON IT —<br />
                  YOU WIN THE BONANZA POOL OF {prize} SOL!!!!
                </div>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 24, background: '#111', border: '2px solid #c0392b', borderRadius: 12, padding: '20px 32px', maxWidth: 600, width: '100%', margin: '24px auto 0 auto', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, color: '#E05A5A', letterSpacing: 3, marginBottom: 16, lineHeight: 1.3 }}>
                🐔 SOMEONE IS ABOUT TO WIN THE BONANZA CHICKEN DROP!!!
              </div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 14, color: '#aaa', lineHeight: 2, marginBottom: 16 }}>
                HOW DO YOU GET A SQUARE ON CHICKEN DROP TO WIN THE BONANZA?<br />
                <span style={{ color: '#F0B800', fontWeight: 700 }}>PLAY CHICKEN DROPPINGS AND MAKE THE TOP 100 LEADERBOARD!!</span>
              </div>
            </div>
          )}

          <div style={{ background: '#111', border: '1px solid #2a2a2a', borderRadius: 12, padding: 20, maxWidth: 400, width: '100%', margin: '24px auto 0 auto' }}>
            <div style={{ fontSize: 11, color: '#F0B800', letterSpacing: 2, marginBottom: 12, textAlign: 'center' }}>
              TOP 100 — CHICKEN DROPPINGS LEADERBOARD
            </div>
            {(() => {
              const lb = (droppingsLeaderboard || []).slice(0, 100);
              const playerIdx = lb.findIndex(e => e.wallet === playerWallet || e.fullWallet === playerWallet);
              return (
                <>
                  {playerWallet && playerIdx >= 0 && (
                    <div style={{ background: 'rgba(240,184,0,0.15)', border: '1px solid #F0B800', borderRadius: 6, padding: '6px 10px', marginBottom: 8, fontSize: 12, display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#F0B800' }}>YOUR POSITION: #{playerIdx + 1} {lb[playerIdx]?.wallet}</span>
                      <span style={{ color: '#4CAF50' }}>{(lb[playerIdx]?.score || 0).toLocaleString()} pts</span>
                    </div>
                  )}
                  {playerWallet && playerIdx === -1 && (
                    <div style={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 6, padding: '6px 10px', marginBottom: 8, fontSize: 11, color: '#888', textAlign: 'center' }}>
                      You are not yet in the top 100 — play Chicken Droppings to qualify!
                    </div>
                  )}
                  <div style={{ maxHeight: 250, overflowY: 'auto' }}>
                    {lb.length === 0 && (
                      <div style={{ color: '#444', fontSize: 11, textAlign: 'center', padding: 16 }}>No scores yet — be the first!</div>
                    )}
                    {lb.map((p, i) => (
                      <div key={i} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '4px 8px',
                        fontSize: 11,
                        color: (p.wallet === playerWallet || p.fullWallet === playerWallet) ? '#F0B800' : i < 3 ? '#F0B800' : '#bbb',
                        background: (p.wallet === playerWallet || p.fullWallet === playerWallet) ? 'rgba(240,184,0,0.1)' : i % 2 === 0 ? '#111' : '#0a0a0a',
                        borderRadius: 4,
                        border: (p.wallet === playerWallet || p.fullWallet === playerWallet) ? '1px solid rgba(240,184,0,0.3)' : 'none',
                      }}>
                        <span style={{ minWidth: 28, color: i < 3 ? '#F0B800' : '#555' }}>#{i + 1}</span>
                        <span style={{ flex: 1 }}>{p?.wallet || `Player ${i + 1}`}</span>
                        <span>{(p?.score || 0).toLocaleString()} pts</span>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>

          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: '#888', marginBottom: 12, lineHeight: 1.8 }}>
              Miss this round? No worries — play Chicken Droppings now to get in the queue<br />
              for the next Progressive Bonanza round!
            </div>
            <a href="/" style={{
              display: 'inline-block',
              background: '#c0392b',
              color: 'white',
              padding: '12px 32px',
              borderRadius: 8,
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: 15,
              fontFamily: "'DM Mono', monospace",
              letterSpacing: '1px',
            }}>
              🐓 PLAY CHICKEN DROPPINGS
            </a>
          </div>

          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#444', textAlign: 'center', marginTop: 12, lineHeight: 1.6 }}>
            Music track: Spaceship by Lukrembo<br/>
            Source: https://freetouse.com/music<br/>
            Free To Use Music for Videos
          </div>

        </div>
      )}

      {/* RUNNING - chicken picks winner */}
      {phase === 'running' && (
        <div style={{ textAlign: 'center', width: '100%', maxWidth: 600 }}>
          <div style={{ fontSize: 20, color: '#F0B800', letterSpacing: 4, marginBottom: 16 }}>🐔 THE CHICKEN IS CHOOSING...</div>
          <div style={{ fontSize: 14, color: '#888', marginBottom: 24 }}>Prize: {prize} SOL</div>

          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '480px', height: '480px', border: '2px solid #2a2a2a', borderRadius: 12, background: '#111' }}>
            {Array.from({ length: 100 }, (_, i) => {
              const num = i + 1;
              const col = i % COLS;
              const row = Math.floor(i / COLS);
              const x = col * CELL;
              const y = row * CELL;
              const isVisiting = walkPath[chickenPos - 1] === num;
              const isWinner = num === winner;
              const isDropQualifier = false;
              return (
                <g key={num}>
                  <rect x={x + 1} y={y + 1} width={CELL - 2} height={CELL - 2}
                    fill={isWinner ? '#F0B800' : isVisiting ? '#F0B80044' : isDropQualifier ? '#2980b922' : '#1a1a1a'}
                    stroke={isWinner ? '#c8960a' : isVisiting ? '#F0B800' : '#2a2a2a'}
                    strokeWidth={isWinner || isVisiting ? 2 : 0.5} rx="2"
                  />
                  <text x={x + CELL / 2} y={y + CELL / 2 - 4} textAnchor="middle" fontSize="7"
                    fill={isWinner ? '#000' : isDropQualifier ? '#5AAAE0' : '#444'} fontWeight={isWinner ? 'bold' : 'normal'}>
                    {isWinner ? '🥚' : num}
                  </text>
                  {shuffledPlayers[i] && (
                    <text x={x + CELL / 2} y={y + CELL / 2 + 8} textAnchor="middle" fontSize="6"
                      fill={isWinner ? '#000' : '#666'}>
                      {shuffledPlayers[i].wallet}
                    </text>
                  )}
                </g>
              );
            })}
            {walkPath[chickenPos - 1] && !winner && (() => {
              const sq = walkPath[chickenPos - 1];
              const col = (sq - 1) % COLS;
              const row = Math.floor((sq - 1) / COLS);
              const cx = col * CELL + CELL / 2;
              const cy = row * CELL + CELL / 2;
              return (
                <g transform={`translate(${cx}, ${cy})`}>
                  <ellipse cx="0" cy="0" rx="16" ry="13" fill="#c8a882" />
                  <polygon points="12,-6 16,-10 18,-5 14,-2" fill="#c0392b" />
                  <circle cx="14" cy="-5" r="3" fill="white" />
                  <circle cx="15" cy="-5" r="2" fill="#1a1a1a" />
                  <polygon points="18,-4 24,-3 18,-2" fill="#e8890a" />
                </g>
              );
            })()}
          </svg>

          <div style={{ marginTop: 16, fontSize: 13, color: '#555' }}>
            Top 100 Chicken Droppings scores qualify for the Bonanza
          </div>
        </div>
      )}

      {/* COMPLETE */}
      {phase === 'complete' && (
        <div style={{ textAlign: 'center', animation: 'bonanzaPulse 1s ease infinite' }}>
          <div style={{ fontSize: 80, marginBottom: 16, animation: 'eggDrop 0.5s ease forwards' }}>🥚</div>
          <div style={{ fontSize: 48, fontWeight: 900, color: '#F0B800', letterSpacing: 4, marginBottom: 8 }}>BONANZA WINNER!</div>
          <div style={{ fontSize: 28, color: '#fff', marginBottom: 8 }}>
            {winnerInfo?.wallet || `Player in spot #${winner}`}
          </div>
          <div style={{ fontSize: 20, color: '#4CAF50', marginBottom: 32 }}>
            Wins {prize} SOL!
          </div>
          <div style={{ fontSize: 13, color: '#666', marginBottom: 32 }}>
            All leaderboards will reset · New pool starting at 0 SOL
          </div>
          <button
            onClick={() => { if (onBonanzaEnd) onBonanzaEnd(); }}
            style={{ background: '#F0B800', color: '#000', border: 'none', borderRadius: 8, padding: '14px 40px', fontSize: 16, fontWeight: 700, cursor: 'pointer', letterSpacing: 2 }}
          >
            START NEW SEASON →
          </button>
        </div>
      )}
    </div>
  );
}
